import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import AppSidebar, { SB_W, SB_W_COLLAPSED } from "../../../AppSidebar";
import { defectsApi } from "../api/defectsApi";
import { buildDefectsQueryParams, computeRangeDates } from "../services/defectsQuery";
import { useDefectsData } from "../hooks/useDefectsData";
import { useDefectsViewDerived } from "../hooks/useDefectsViewDerived";
import DefectDrawer from "../components/DefectDrawer";
import { FilterTriggerButton, InlineCell, StatusTransitionReasonModal } from "../../common/components";
import { listTags } from "../../common/services/tagsApi";
import { listFilterTemplates } from "../../common/services/filterTemplatesApi";
import SavedFiltersPanel from "../../common/components/SavedFiltersPanel";
import {
  DEFECT_FILTER_FIELDS,
  DEFAULT_DEFECT_APPLIED_FILTERS,
  ALLOWED_TRANSITIONS,
  ageDays,
  defectId,
  getDefectFieldValue,
} from "../services/defectsViewDerivations";
import "../../../DefectsViewV2.css";
import "../../../V2SideDrawer.css";
import "../../../MasterViewV2.css";
import { ORG_LABEL } from "../../../branding";

// Defect statuses requiring a reason via POST /defects/{id}/transition
// (see defectsApi.js's transitionStatus() comment for why this endpoint,
// not the generic PATCH, is used for inline status edits).
const STATUS_REASON_REQUIRED = new Set(["Rejected", "Re-opened"]);

// ── Constants ──────────────────────────────────────────────────────────────────
const SEV_CLS = {
  "Blocker":  "sev-blkr",
  "Critical": "sev-crit",
  "High":     "sev-high",
  "Medium":   "sev-med",
  "Low":      "sev-low",
};
const SEV_ABBR = {
  "Blocker":  "Blkr",
  "Critical": "Crit",
  "High":     "High",
  "Medium":   "Med",
  "Low":      "Low",
};
const ST_CLS = {
  "Open":             "st-open",
  "In Progress":      "st-prog",
  "Fixed":            "st-fix",
  "To Be Tested":     "st-test",
  "Re-opened":        "st-reop",
  "Closed":           "st-closed",
  "Rejected":         "st-rej",
  "Needs Discussion": "st-disc",
};
const ST_ABBR = {
  "Open":             "Open",
  "In Progress":      "In Progress",
  "Fixed":            "Fixed",
  "To Be Tested":     "To Test",
  "Re-opened":        "Reopened",
  "Closed":           "Closed",
  "Rejected":         "Rejected",
  "Needs Discussion": "Discuss",
};
const ENV_CLS = {
  "Dev":        "e-dev",
  "QA":         "e-qa",
  "UAT":        "e-uat",
  "Staging":    "e-uat",
  "Production": "e-prod",
};
const ENV_ABBR = {
  "Dev":        "Dev",
  "QA":         "QA",
  "UAT":        "UAT",
  "Staging":    "UAT",
  "Production": "Prod",
};
const ISSUE_OPTIONAL_COLUMNS = [
  { key: "severity",    label: "Severity" },
  { key: "priority",    label: "Priority" },
  { key: "type",        label: "Type" },
  { key: "status",      label: "Status" },
  { key: "environment", label: "Environment" },
  { key: "visibility",  label: "Visibility" },
  { key: "source",             label: "Source" },
  { key: "reporting_channel",  label: "Reporting Channel" },
  { key: "reporter",    label: "Reporter" },
  { key: "owner",       label: "Owner" },
  { key: "team",        label: "Team" },
  { key: "project",     label: "Project" },
  { key: "customer",    label: "Customer" },
  { key: "aging",       label: "Aging" },
  { key: "created",     label: "Created" },
  { key: "updated",     label: "Updated" },
  { key: "due",         label: "Due" },
  { key: "closed",      label: "Closed" },
];

function escHtml(s) {
  return String(s ?? "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;").replace(/"/g, "&quot;");
}

const VIS_CLS = { Public: "vis-pub", Internal: "vis-int", Private: "vis-priv" };
function VisPill({ visibility }) {
  const v = String(visibility || "Public");
  return <span className={`dfv2-vispill ${VIS_CLS[v] || "vis-pub"}`}>{v}</span>;
}

function normalizeDefectVisibility(v) {
  const s = String(v || "").trim().toLowerCase();
  if (s === "private")  return "Private";
  if (s === "internal") return "Internal";
  return "Public";
}

export function canViewDefectByVisibility(defect, user) {
  const vis = normalizeDefectVisibility(defect?.visibility);
  if (vis === "Public") return true;
  const isExternal = (() => {
    const t = String(user?.db_user_type || "").trim().toLowerCase();
    return t === "external" || t === "external user";
  })();
  if (vis === "Internal") return !isExternal;
  // Private: only reporter or assignee
  const uid = user?.db_user_id;
  return uid && (defect.reported_by === uid || defect.assigned_to === uid);
}

// Gate for the "Executive Summary" tab — mirrors the backend's
// _is_admin_or_am_internal() in routers/defects.py exactly.
function isAdminOrAmInternal(user) {
  const role = String(user?.db_role || "").trim().toLowerCase();
  const userType = String(user?.db_user_type || "").trim().toLowerCase();
  return ["admin", "super admin", "am"].includes(role) && ["internal", ""].includes(userType);
}

// Lifecycle bar configuration — 9 status buckets in design order
const LIFECYCLE_STATES = [
  { k: "Open",             lc: "open",   color: "#B03A2E", label: "Open" },
  { k: "In Progress",      lc: "prog",   color: "#b08a3a", label: "In Progress" },
  { k: "Fixed",            lc: "fix",    color: "#8B5A9B", label: "Fixed" },
  { k: "To Be Tested",     lc: "test",   color: "#2E5C8A", label: "To Test" },
  { k: "Closed",           lc: "closed", color: "#2F7D4F", label: "Closed" },
  { k: "Re-opened",        lc: "reop",   color: "#a53038", label: "Reopened" },
  { k: "Rejected",         lc: "rej",    color: "#777",    label: "Rejected" },
  { k: "Needs Discussion", lc: "disc",   color: "#a9a9a9", label: "Discuss" },
];
export const DEFECT_STATUS_COLOR = Object.fromEntries(
  LIFECYCLE_STATES.map((s) => [s.k, s.color])
);

// ── Helpers ────────────────────────────────────────────────────────────────────
function ageClass(days) {
  if (days <= 3) return "fresh";
  if (days <= 7) return "warm";
  return "hot";
}

const MONTHS = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
function fmtDate(d) {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")} ${MONTHS[dt.getMonth()]}`;
}

function fmtDateTime(d) {
  if (!d) return "—";
  const dt = new Date(d);
  const dd = String(dt.getDate()).padStart(2, "0");
  const mon = MONTHS[dt.getMonth()];
  const hh = String(dt.getHours()).padStart(2, "0");
  const min = String(dt.getMinutes()).padStart(2, "0");
  return `${dd} ${mon} ${hh}:${min}`;
}

function initials(name) {
  if (!name) return "?";
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

function avatarColor(name) {
  if (!name) return 1;
  let h = 0;
  for (let i = 0; i < name.length; i++) h = (h * 31 + name.charCodeAt(i)) & 0xffff;
  return (h % 5) + 1;
}

// ── Pure pill/badge components ─────────────────────────────────────────────────
function SevPip({ severity }) {
  const cls = SEV_CLS[severity] || "sev-low";
  const lbl = SEV_ABBR[severity] || severity || "—";
  return (
    <span className={`dfv2-pip ${cls}`}>
      <span className="dot" />
      {lbl}
    </span>
  );
}

function StPill({ status }) {
  const cls = ST_CLS[status] || "st-open";
  const lbl = ST_ABBR[status] || status || "—";
  return (
    <span className={`dfv2-stpill ${cls}`}>
      <span className="ic" />
      {lbl}
    </span>
  );
}

function EnvChip({ env }) {
  const cls = ENV_CLS[env] || "e-dev";
  const lbl = ENV_ABBR[env] || env || "—";
  return <span className={`dfv2-env ${cls}`}>{lbl}</span>;
}

function PersonCell({ name, isMe = false }) {
  const ac = avatarColor(name);
  return (
    <div className="dfv2-people">
      <span className={`dfv2-avatar ac-${ac}`}>{initials(name)}</span>
      <span>{name || "—"}</span>
      {isMe && <span className="me-tag">ME</span>}
    </div>
  );
}

function AgingCell({ days }) {
  const cls = ageClass(days);
  const pct = Math.min(100, (days / 14) * 100);
  return (
    <div className={`dfv2-aging ${cls}`}>
      <span className="num">{days}<span className="u">d</span></span>
      <span className="bar"><span style={{ width: `${pct}%` }} /></span>
      <span className="ticks"><span>0</span><span>7d</span><span>14d+</span></span>
    </div>
  );
}

// ── SVG Donut helpers ──────────────────────────────────────────────────────────
function polarToXY(cx, cy, r, angleDeg) {
  const rad = (angleDeg - 90) * Math.PI / 180;
  return { x: cx + r * Math.cos(rad), y: cy + r * Math.sin(rad) };
}

function annularArcPath(cx, cy, r, sw, startDeg, endDeg) {
  if (endDeg - startDeg < 0.3) return "";
  const p1 = polarToXY(cx, cy, r, startDeg);
  const p2 = polarToXY(cx, cy, r, endDeg);
  const p3 = polarToXY(cx, cy, r - sw, endDeg);
  const p4 = polarToXY(cx, cy, r - sw, startDeg);
  const large = (endDeg - startDeg) > 180 ? 1 : 0;
  return [
    `M ${p1.x.toFixed(2)} ${p1.y.toFixed(2)}`,
    `A ${r} ${r} 0 ${large} 1 ${p2.x.toFixed(2)} ${p2.y.toFixed(2)}`,
    `L ${p3.x.toFixed(2)} ${p3.y.toFixed(2)}`,
    `A ${r - sw} ${r - sw} 0 ${large} 0 ${p4.x.toFixed(2)} ${p4.y.toFixed(2)}`,
    "Z",
  ].join(" ");
}

// ── Lifecycle Bar ──────────────────────────────────────────────────────────────
function LifecycleBar({ statusCounts, onSelect, selected }) {
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  const tipRef = useRef(null);

  const showTip = useCallback((el, state) => {
    const tip = tipRef.current;
    if (!tip) return;
    const n = statusCounts[state.k] || 0;
    const pct = total > 0 ? ((n / total) * 100).toFixed(1) : "0.0";
    tip.innerHTML = `
      <div class="tt-title" style="color:${escHtml(state.color)}">${escHtml(state.label)}</div>
      <div class="tt-sub">${n} defects · ${pct}%</div>
      <div class="tt-row"><span class="sw" style="background:${escHtml(state.color)}"></span><span class="k">Count</span><span class="v">${n}</span></div>
    `;
    tip.classList.add("on");
    const rect = el.getBoundingClientRect();
    const wrap = el.closest(".dfv2-lifebar-wrap").getBoundingClientRect();
    const x = rect.left + rect.width / 2 - wrap.left;
    tip.style.left = `${Math.max(8, Math.min(x - 90, wrap.width - 220))}px`;
    tip.style.top = "44px";
  }, [statusCounts, total]);

  const hideTip = () => {
    if (tipRef.current) tipRef.current.classList.remove("on");
  };

  return (
    <div className="dfv2-lifecycle-wrap">
      <div className="dfv2-lifecycle-head">
        <h3>Lifecycle</h3>
        <span className="lc-rule" />
        <span className="hint">
          Flow → <b>OPEN</b> → in progress → fixed → to test → closed
        </span>
      </div>
      <div className="dfv2-lifebar-wrap">
        <div className="dfv2-lifebar">
          {LIFECYCLE_STATES.map((state) => {
            const n = statusCounts[state.k] || 0;
            const flex = total > 0 ? n : 1;
            const isSmall = total > 0 && (n / total) < 0.03;
            return (
              <div
                key={state.k}
                className={`seg${isSmall ? " small" : ""}${selected === state.k ? " on" : ""}`}
                style={{ flex: `${flex} 1 0`, background: state.color }}
                onClick={() => onSelect(selected === state.k ? null : state.k)}
                onMouseEnter={(e) => showTip(e.currentTarget, state)}
                onMouseLeave={hideTip}
              >
                <span className="n">{n}</span>
              </div>
            );
          })}
        </div>
        <div ref={tipRef} className="dfv2-lc-tip" />
      </div>
      <div className="dfv2-lifebar-legend">
        {LIFECYCLE_STATES.map((state) => {
          const n = statusCounts[state.k] || 0;
          const pct = total > 0 ? ((n / total) * 100).toFixed(1) : "0.0";
          return (
            <div
              key={state.k}
              className={`lg${selected === state.k ? " on" : ""}`}
              style={{ "--lg-color": state.color }}
              onClick={() => onSelect(selected === state.k ? null : state.k)}
            >
              <span className="lb">{state.label}</span>
              <span className="n">{n}</span>
              <span className="pct">{pct}%</span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── KPI Strip ──────────────────────────────────────────────────────────────────
function KPIStrip({ stats, rangeLabel }) {
  const { open, critOpen, slaBreach, slaPct, avgAge, fixRate, openPrev } = stats;
  const openDelta = openPrev !== undefined ? open - openPrev : null;

  return (
    <div className="dfv2-kpis">
      <div className="k">
        <span className="lbl">Open</span>
        <span className="v">{open}</span>
        <span className="sub">
          in flight
          {openDelta !== null && openDelta !== 0 && (
            <> · <span className={openDelta > 0 ? "up" : "dn"}>
              {openDelta > 0 ? `+${openDelta}` : openDelta} this period
            </span></>
          )}
        </span>
      </div>
      <div className="k">
        <span className="lbl">Critical open</span>
        <span className="v red">
          {critOpen}
          <span className="u">/ {open}</span>
        </span>
        <span className="sub">Critical &amp; Blocker</span>
      </div>
      <div className="k">
        <span className="lbl">SLA breach</span>
        <span className="v amber">
          {slaPct.toFixed(1)}
          <span className="u">%</span>
        </span>
        <span className="sub">{slaBreach} past due date</span>
      </div>
      <div className="k">
        <span className="lbl">Avg age · open</span>
        <span className="v">
          {avgAge.toFixed(1)}
          <span className="u">days</span>
        </span>
        <span className="sub">of all open defects</span>
      </div>
      <div className="k">
        <span className="lbl">Fix rate · {rangeLabel}</span>
        <span className="v">
          {fixRate === null ? "—" : fixRate.toFixed(2)}
          <span className="u">x</span>
        </span>
        <span className="sub">
          closed / opened
          {fixRate !== null && (
            <> · <span className={fixRate >= 1 ? "dn" : "up"}>{fixRate >= 1 ? "healthy" : "backlog growing"}</span></>
          )}
        </span>
      </div>
    </div>
  );
}

// ── Combo Chart (inflow vs outflow) ───────────────────────────────────────────
function ComboChart({ data }) {
  const svgRef = useRef(null);
  const tipRef = useRef(null);

  const W = 1100, H = 440;
  const pad = { top: 28, right: 20, bottom: 38, left: 52 };
  const cW = W - pad.left - pad.right;
  const cH = H - pad.top - pad.bottom;

  const n = data.length;
  const maxBar  = Math.max(...data.map(d => Math.max(d.opened, d.closed)), 1);
  const maxLine = Math.max(...data.map(d => Math.max(d.cumOpened, d.cumClosed)), 1);
  const barW = cW / Math.max(n, 1);
  const halfW = barW * 0.38;

  const xOf   = (i) => pad.left + i * barW + barW / 2;
  const yBar  = (v) => pad.top + cH - (v / maxBar) * cH;
  const yLine = (v) => pad.top + cH * 0.92 - (v / maxLine) * (cH * 0.68);

  const showEvery = n <= 7 ? 1 : n <= 14 ? 2 : n <= 30 ? 3 : 7;
  const yTicks = 5;

  const lineOpenPath  = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(1)} ${yLine(d.cumOpened).toFixed(1)}`).join(" ");
  const lineClosePath = data.map((d, i) => `${i === 0 ? "M" : "L"} ${xOf(i).toFixed(1)} ${yLine(d.cumClosed).toFixed(1)}`).join(" ");

  const handleMouseMove = useCallback((e) => {
    if (!svgRef.current || n === 0) return;
    const rect = svgRef.current.getBoundingClientRect();
    const svgX  = (e.clientX - rect.left) / rect.width * W;
    const idx   = Math.min(n - 1, Math.max(0, Math.floor((svgX - pad.left) / barW)));
    const d     = data[idx];
    if (!d || !tipRef.current) return;
    tipRef.current.innerHTML = `
      <div class="tt-date">${escHtml(d.label)}</div>
      <div class="tt-row"><span class="sw" style="background:#b08a3a"></span><span class="k">New</span><span class="v">${d.opened}</span></div>
      <div class="tt-row"><span class="sw" style="background:#4a7c5c"></span><span class="k">Closed</span><span class="v">${d.closed}</span></div>
      <div class="tt-row"><span class="sw ln" style="background:#8B5CF6"></span><span class="k">Cum. opened</span><span class="v">${d.cumOpened}</span></div>
      <div class="tt-row"><span class="sw ln" style="background:#3B9EFF"></span><span class="k">Cum. closed</span><span class="v">${d.cumClosed}</span></div>
    `;
    tipRef.current.classList.add("on");
    const tipX = e.clientX - rect.left + 14;
    const tipY = e.clientY - rect.top - 10;
    tipRef.current.style.left = `${Math.min(tipX, rect.width - 200)}px`;
    tipRef.current.style.top  = `${Math.max(8, tipY)}px`;
  }, [data, n, barW, W, pad.left]);

  const handleMouseLeave = () => {
    if (tipRef.current) tipRef.current.classList.remove("on");
  };

  return (
    <div className="dfv2-card" style={{ marginBottom: 22 }}>
      <div className="ch-head">
        <h3>Inflow vs outflow</h3>
        <span className="sub">Daily · selected range</span>
      </div>
      <div className="dfv2-legend">
        <span className="leg bar-open"><span className="swbar" />New today</span>
        <span className="leg bar-close"><span className="swbar" />Closed today</span>
        <span className="leg line-open"><span className="swline" />Cum. opened</span>
        <span className="leg line-close"><span className="swline" />Cum. closed</span>
      </div>
      <div className="dfv2-combo-wrap" onMouseMove={handleMouseMove} onMouseLeave={handleMouseLeave}>
        <svg
          ref={svgRef}
          className="dfv2-combo"
          viewBox={`0 0 ${W} ${H}`}
          preserveAspectRatio="none"
        >
          {/* Grid lines + Y axis */}
          {Array.from({ length: yTicks + 1 }, (_, j) => {
            const y = pad.top + (cH / yTicks) * j;
            const val = Math.round(maxBar * (1 - j / yTicks));
            return (
              <g key={j}>
                <line x1={pad.left} x2={W - pad.right} y1={y} y2={y} stroke="var(--dfv2-rule)" strokeWidth={1} />
                {val > 0 && (
                  <text x={pad.left - 6} y={y + 4} textAnchor="end" fill="var(--dfv2-ink-4)" fontFamily="JetBrains Mono, monospace" fontSize={10}>{val}</text>
                )}
              </g>
            );
          })}
          {/* Bars */}
          {data.map((d, i) => {
            const barHeight1 = Math.max(0, cH - (yBar(d.opened) - pad.top));
            const barHeight2 = Math.max(0, cH - (yBar(d.closed) - pad.top));
            return (
              <g key={i}>
                {d.opened > 0 && (
                  <rect
                    x={xOf(i) - halfW}
                    y={yBar(d.opened)}
                    width={Math.max(1, halfW - 1)}
                    height={barHeight1}
                    fill="#b08a3a"
                    fillOpacity={0.92}
                  />
                )}
                {d.closed > 0 && (
                  <rect
                    x={xOf(i) + 1}
                    y={yBar(d.closed)}
                    width={Math.max(1, halfW - 1)}
                    height={barHeight2}
                    fill="#4a7c5c"
                    fillOpacity={0.92}
                  />
                )}
                {i % showEvery === 0 && (
                  <text
                    x={xOf(i)}
                    y={H - pad.bottom + 14}
                    textAnchor="middle"
                    fill="var(--dfv2-ink-4)"
                    fontFamily="JetBrains Mono, monospace"
                    fontSize={10}
                  >{d.label}</text>
                )}
              </g>
            );
          })}
          {/* Baseline */}
          <line x1={pad.left} x2={W - pad.right} y1={pad.top + cH} y2={pad.top + cH} stroke="var(--dfv2-rule)" strokeWidth={1} />
          {/* Cumulative lines */}
          {n > 0 && (
            <>
              <path d={lineOpenPath}  stroke="#8B5CF6" strokeWidth={2.4} fill="none" strokeLinejoin="round" />
              <path d={lineClosePath} stroke="#3B9EFF" strokeWidth={2.4} fill="none" strokeLinejoin="round" />
            </>
          )}
          {n === 0 && (
            <text x={W / 2} y={H / 2} textAnchor="middle" fill="var(--dfv2-ink-4)" fontFamily="JetBrains Mono, monospace" fontSize={12} letterSpacing="0.1em">NO DATA FOR SELECTED RANGE</text>
          )}
        </svg>
        <div ref={tipRef} className="dfv2-tooltip" />
      </div>
    </div>
  );
}

// ── Aging Bar ──────────────────────────────────────────────────────────────────
function AgingBar({ buckets, openCount }) {
  const { b0, b1, b2, b3 } = buckets;
  const segs = [
    { cls: "s1", n: b0, label: "0–7d" },
    { cls: "s2", n: b1, label: "8–14d" },
    { cls: "s3", n: b2, label: "15–30d" },
    { cls: "s4", n: b3, label: "30d+" },
  ];
  return (
    <div className="dfv2-card" style={{ marginBottom: 22 }}>
      <div className="ch-head">
        <h3>Aging · open only</h3>
        <span className="sub">Days since reported · {openCount} open defects</span>
      </div>
      <div className="dfv2-aging-wrap">
        <div className="dfv2-aging-bar">
          {segs.map(s => (
            <div
              key={s.cls}
              className={`seg ${s.cls}`}
              style={{ flex: `${s.n || 0.5} 1 0` }}
            >
              {s.n > 0 && s.n}
            </div>
          ))}
        </div>
        <div className="dfv2-aging-legend">
          {segs.map(s => (
            <span key={s.cls}><b>{s.n}</b>{s.label}</span>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Status Mix ────────────────────────────────────────────────────────────────
function StatusMixList({ statusCounts }) {
  const total = Object.values(statusCounts).reduce((a, b) => a + b, 0);
  return (
    <div className="dfv2-card">
      <div className="ch-head">
        <h3>Status mix</h3>
        <span className="sub">All defects · lifecycle</span>
      </div>
      <div className="dfv2-status-list">
        {LIFECYCLE_STATES.map(({ k, label, color }) => {
          const n = statusCounts[k] || 0;
          const pct = total > 0 ? ((n / total) * 100).toFixed(0) : 0;
          return (
            <div key={k} className="dfv2-status-row">
              <span className="lbl">
                <span className="dot" style={{ background: color }} />
                {label}
              </span>
              <div className="sbar">
                <div className="fill" style={{ width: `${pct}%`, background: color }} />
              </div>
              <span className="n">
                {n}
                <span className="pct">{pct}%</span>
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ── Top Modules ───────────────────────────────────────────────────────────────
function TopModulesList({ modules }) {
  const maxN = Math.max(...modules.map(m => m.n), 1);
  return (
    <div className="dfv2-card">
      <div className="ch-head">
        <h3>Top modules by open defects</h3>
        <span className="sub">Critical shaded red</span>
      </div>
      <div className="dfv2-modules">
        {modules.slice(0, 10).map((m, i) => {
          const pct = (m.n / maxN) * 100;
          const fillCls = m.hasCrit ? "crit" : m.hasHigh ? "high" : "";
          return (
            <div key={i} className="dfv2-mod-row">
              <span className="name">
                {m.name}
                {m.code && <span className="code">{m.code}</span>}
              </span>
              <div className="hbar">
                <div className={`fill ${fillCls}`} style={{ width: `${pct}%` }} />
              </div>
              <span className="n">{m.n}</span>
            </div>
          );
        })}
        {modules.length === 0 && (
          <div className="dfv2-empty" style={{ padding: 16 }}>No data</div>
        )}
      </div>
    </div>
  );
}

// ── Breakdown Nested Donut ─────────────────────────────────────────────────────
function BreakdownDonut({ sevData, totalCount }) {
  const [hoverKey, setHoverKey] = useState(null);
  const tipRef = useRef(null);
  const cx = 230, cy = 230;
  const outerR = 185, outerSW = 44;
  const innerR = 123, innerSW = 40;
  const GAP_DEG = 0.6;

  // Build outer arcs (severity)
  const outerArcs = [];
  let sevOffset = 0;
  sevData.forEach(sev => {
    if (!sev.n || sev.n === 0) return;
    const frac = sev.n / totalCount;
    const startDeg = sevOffset * 360;
    const endDeg   = (sevOffset + frac) * 360 - GAP_DEG;
    outerArcs.push({ ...sev, startDeg, endDeg });
    sevOffset += frac;
  });

  // Build inner arcs — each severity's projects fill exactly its outer arc angle,
  // regardless of how many projects are shown (top-5 slice). This guarantees the
  // inner ring is always a complete circle, symmetrical with the outer ring.
  const innerArcs = [];
  let sevOffsetI = 0;
  sevData.forEach(sev => {
    if (!sev.n || sev.n === 0) return;
    const sevFrac  = sev.n / totalCount;
    const projects = (sev.projects || []).filter(p => p.n > 0);
    const projTot  = projects.reduce((a, p) => a + p.n, 0);
    let localOff   = 0;
    projects.forEach(proj => {
      // Fraction within this severity's arc, then scaled to full circle
      const pFrac    = projTot > 0 ? (proj.n / projTot) * sevFrac : 0;
      const startDeg = (sevOffsetI + localOff) * 360;
      const endDeg   = (sevOffsetI + localOff + pFrac) * 360 - GAP_DEG * 0.5;
      innerArcs.push({ ...proj, sevKey: sev.k, startDeg, endDeg });
      localOff += pFrac;
    });
    sevOffsetI += sevFrac;
  });

  const showTip = (e, d, ring) => {
    setHoverKey(d.k);
    if (!tipRef.current) return;
    const pct = ((d.n / totalCount) * 100).toFixed(1);
    tipRef.current.innerHTML = `
      <div class="k">${escHtml(ring)}</div>
      <div class="ttl">${escHtml(d.lb)}</div>
      <div class="row"><span class="v">${d.n}</span><span class="p">${pct}%</span></div>
    `;
    tipRef.current.classList.add("on");
    const wrap = e.currentTarget.closest(".dfv2-nest-chart").getBoundingClientRect();
    tipRef.current.style.left = `${e.clientX - wrap.left + 12}px`;
    tipRef.current.style.top  = `${e.clientY - wrap.top  - 10}px`;
  };

  const hideTip = () => {
    setHoverKey(null);
    if (tipRef.current) tipRef.current.classList.remove("on");
  };

  return (
    <div className="dfv2-breakdown">
      <div className="dfv2-breakdown-head">
        <h3>Breakdown</h3>
        <span className="eyebrow">Nested by severity → project</span>
        <span className="bd-rule" />
        <span className="tot">Total <b>{totalCount}</b> defects</span>
      </div>
      <div className="dfv2-nest-wrap">
        <div className="dfv2-nest-chart">
          <svg className="dfv2-nest" viewBox="0 0 460 460">
            {/* Outer ring — severity */}
            {outerArcs.map((arc, i) => {
              const path = annularArcPath(cx, cy, outerR, outerSW, arc.startDeg, arc.endDeg);
              const dim = hoverKey !== null && hoverKey !== arc.k;
              return (
                <path
                  key={`outer-${i}`}
                  d={path}
                  fill={arc.c}
                  opacity={dim ? 0.25 : 0.9}
                  style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                  onMouseEnter={(e) => showTip(e, arc, "SEVERITY")}
                  onMouseLeave={hideTip}
                />
              );
            })}
            {/* Inner ring — projects */}
            {innerArcs.map((arc, i) => {
              const path = annularArcPath(cx, cy, innerR, innerSW, arc.startDeg, arc.endDeg);
              const dim = hoverKey !== null && hoverKey !== arc.sevKey;
              return (
                <path
                  key={`inner-${i}`}
                  d={path}
                  fill={arc.c}
                  opacity={dim ? 0.25 : 0.88}
                  style={{ cursor: "pointer", transition: "opacity 0.15s" }}
                  onMouseEnter={(e) => showTip(e, { ...arc, n: arc.n, lb: `${arc.lb} (${arc.sevKey})` }, "PROJECT")}
                  onMouseLeave={hideTip}
                />
              );
            })}
            {/* Center hub */}
            <text x={cx} y={cy - 8} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontWeight={700} fontSize={28} fill="#0B0E13" style={{ fontVariantNumeric: "tabular-nums" }}>{totalCount}</text>
            <text x={cx} y={cy + 14} textAnchor="middle" fontFamily="JetBrains Mono, monospace" fontSize={9} fill="#5C6069" letterSpacing="0.22em">DEFECTS</text>
          </svg>
          <div ref={tipRef} className="dfv2-nest-tip" />
        </div>
        {/* Legend — one column per severity */}
        <div className="dfv2-nest-legend">
          {sevData.map((sev) => (
            <div key={sev.k} className="col">
              <div className="hd" style={{ color: sev.c }}>
                <span className="ring-dot" />
                {sev.lb}
                <span className="n">{sev.n}</span>
              </div>
              <ul>
                {(sev.projects || []).filter(p => p.n > 0).map(proj => {
                  const pPct = sev.n > 0 ? ((proj.n / sev.n) * 100).toFixed(0) : 0;
                  return (
                    <li key={proj.k}>
                      <span className="sw" style={{ background: proj.c }} />
                      <span className="lbl">{proj.lb}</span>
                      <span className="n">{proj.n}</span>
                      <span className="pct">{pPct}%</span>
                    </li>
                  );
                })}
                {(!sev.projects || sev.projects.every(p => !p.n)) && (
                  <li><span className="lbl" style={{ color: "var(--dfv2-ink-4)" }}>No data</span></li>
                )}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Executive Summary ────────────────────────────────────────────────────────
// Snapshot stat cards fed by GET /defects/executive-summary. Colors reuse
// DEFECT_STATUS_COLOR (the same lifecycle-bar palette used elsewhere in this
// file) for status-based cards, so this view stays visually consistent with
// the rest of the Defects module rather than inventing a new palette.
const EXECSUM_RAISED_TODAY_COLOR = "#3B7A78"; // teal — the one metric here that isn't a status

function ExecSummaryCard({ label, value, color }) {
  return (
    <div className="dfv2-execsum-card" style={{ background: `${color}1A`, borderColor: `${color}40` }}>
      <div className="dfv2-execsum-card-value" style={{ color }}>{value}</div>
      <div className="dfv2-execsum-card-label">{label}</div>
    </div>
  );
}

function ExecSummarySection({ title, cards }) {
  return (
    <div className="dfv2-execsum-section">
      <div className="dfv2-execsum-section-title">{title}</div>
      <div className="dfv2-execsum-grid">
        {cards.map((c) => (
          <ExecSummaryCard key={c.label} label={c.label} value={c.value} color={c.color} />
        ))}
      </div>
    </div>
  );
}

function ExecutiveSummary({ data, loading }) {
  if (loading || !data) {
    return <div className="dfv2-execsum dfv2-loading">{loading ? "Loading…" : "No data"}</div>;
  }
  const { overall, codifi, customer } = data;

  const overallCards = [
    { label: "Open",             value: overall.open,             color: DEFECT_STATUS_COLOR["Open"] },
    { label: "In Progress",      value: overall.in_progress,      color: DEFECT_STATUS_COLOR["In Progress"] },
    { label: "Fixed",            value: overall.fixed,            color: DEFECT_STATUS_COLOR["Fixed"] },
    { label: "To Be Tested",     value: overall.to_be_tested,     color: DEFECT_STATUS_COLOR["To Be Tested"] },
    { label: "Re-opened",        value: overall.reopened,         color: DEFECT_STATUS_COLOR["Re-opened"] },
    { label: "Closed Today",     value: overall.closed_today,     color: DEFECT_STATUS_COLOR["Closed"] },
    { label: "Need to Discuss",  value: overall.needs_discussion, color: DEFECT_STATUS_COLOR["Needs Discussion"] },
  ];
  const groupCards = (g) => [
    { label: "Open",         value: g.open,         color: DEFECT_STATUS_COLOR["Open"] },
    { label: "Re-opened",    value: g.reopened,     color: DEFECT_STATUS_COLOR["Re-opened"] },
    { label: "To Be Tested", value: g.to_be_tested, color: DEFECT_STATUS_COLOR["To Be Tested"] },
    { label: "Raised Today", value: g.raised_today, color: EXECSUM_RAISED_TODAY_COLOR },
    { label: "Closed Today", value: g.closed_today, color: DEFECT_STATUS_COLOR["Closed"] },
  ];

  return (
    <div className="dfv2-execsum" style={{ padding: "20px 28px" }}>
      <div className="dfv2-dash-head">
        <h2>Executive Summary <em>— All Projects</em></h2>
        <span className="eyebrow">snapshot · as of {data.as_of}</span>
        <span className="dash-rule" />
      </div>
      <ExecSummarySection title="Overall"  cards={overallCards} />
      <ExecSummarySection title={ORG_LABEL} cards={groupCards(codifi)} />
      <ExecSummarySection title="Customer" cards={groupCards(customer)} />
    </div>
  );
}

// ── Issues Table ───────────────────────────────────────────────────────────────

function IconSearch() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="11" cy="11" r="8" />
      <line x1="21" y1="21" x2="16.65" y2="16.65" />
    </svg>
  );
}

function IconTable() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="18" height="18" rx="2" />
      <line x1="3" y1="9" x2="21" y2="9" />
      <line x1="3" y1="15" x2="21" y2="15" />
      <line x1="9" y1="9" x2="9" y2="21" />
    </svg>
  );
}

function IconKanban() {
  return (
    <svg viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="5" height="18" rx="1" />
      <rect x="10" y="3" width="5" height="12" rx="1" />
      <rect x="17" y="3" width="5" height="15" rx="1" />
    </svg>
  );
}

function IconDownload() {
  return (
    <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
      <polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/>
    </svg>
  );
}

function isFieldActive(field, appliedFilters) {
  const val = appliedFilters?.[field.key];
  if (!val) return false;
  if (field.type === "text") return !!val.value?.trim();
  if (field.type === "daterange") return !!(val.from || val.to);
  return Array.isArray(val.values) && val.values.length > 0;
}

function DefectAdvancedFilterPopover({ open, defects, optionsDefects, teams, appliedFilters, filterMode, onApply, onClose, onApplyTemplate }) {
  const [draft, setDraft] = useState({});
  const [mode, setMode] = useState("all");
  const [search, setSearch] = useState("");
  const [expanded, setExpanded] = useState(null);
  const [fieldSearch, setFieldSearch] = useState({});
  const [tagQuery, setTagQuery] = useState("");
  const [tagSuggestions, setTagSuggestions] = useState([]);
  const [tagLoadingSuggest, setTagLoadingSuggest] = useState(false);

  useEffect(() => {
    if (!open) return;
    setDraft(JSON.parse(JSON.stringify(appliedFilters || {})));
    setMode(filterMode || "all");
    setSearch("");
    setExpanded(null);
    setFieldSearch({});
    setTagQuery("");
    setTagSuggestions([]);
  }, [open, appliedFilters, filterMode]);

  useEffect(() => {
    const q = tagQuery.trim();
    if (!q) { setTagSuggestions([]); return; }
    let active = true;
    setTagLoadingSuggest(true);
    listTags({ search: q })
      .then((rows) => { if (active) setTagSuggestions(Array.isArray(rows) ? rows : []); })
      .catch(() => { if (active) setTagSuggestions([]); })
      .finally(() => { if (active) setTagLoadingSuggest(false); });
    return () => { active = false; };
  }, [tagQuery]);

  const fields = useMemo(() => {
    return DEFECT_FILTER_FIELDS.map((f) => {
      if (f.type !== "choice") return { ...f, options: [] };
      let options = [];
      if (f.key === "reported_team_id" || f.key === "owning_team_id") {
        options = (teams || [])
          .map((t) => ({
            value: String(t.id || "").trim(),
            label: String(t.team_name || "").trim(),
          }))
          .filter((t) => t.value && t.label)
          .sort((a, b) => a.label.localeCompare(b.label));
      } else if (f.key === "tags") {
        options = [];  // populated dynamically via search
      } else {
        // Sourced from the optionsDefects prop (fetched independently of
        // appliedFilters), NOT the currently-filtered `defects` — otherwise
        // applying this same field's own filter would shrink the dataset
        // used to build its own option list, permanently narrowing it to
        // just the already-selected value(s) until every filter was reset.
        const rawOpts = f.staticOptions
          || [...new Set((optionsDefects || []).map((d) => String(getDefectFieldValue(d, f.key) || "").trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b));
        options = rawOpts.map((v) => ({ value: v, label: v }));
      }
      return { ...f, options };
    });
  }, [optionsDefects, teams]);

  const visibleFields = useMemo(() => {
    const base = search.trim()
      ? fields.filter((f) => f.label.toLowerCase().includes(search.toLowerCase()))
      : fields;
    return [...base].sort((a, b) => {
      const aActive = isFieldActive(a, appliedFilters);
      const bActive = isFieldActive(b, appliedFilters);
      if (aActive === bActive) return 0;
      return aActive ? -1 : 1;
    });
  }, [fields, search, appliedFilters]);

  const setOp = (key, op) =>
    setDraft((d) => ({ ...d, [key]: { op, values: d[key]?.values || [] } }));

  const toggleVal = (key, val) =>
    setDraft((d) => {
      const curr = d[key]?.values || [];
      const next = curr.includes(val) ? curr.filter((v) => v !== val) : [...curr, val];
      return { ...d, [key]: { op: d[key]?.op || "is", values: next } };
    });
  const clearField = (key) =>
    setDraft((d) => {
      const next = { ...d };
      delete next[key];
      return next;
    });

  const handleReset = () => {
    setDraft({});
    setMode("all");
  };

  const handleApply = () => {
    onApply(draft, mode);
    onClose();
  };

  useEffect(() => {
    if (!open) return undefined;
    const onEsc = (e) => {
      if (e.key === "Escape") onClose?.();
    };
    window.addEventListener("keydown", onEsc);
    return () => window.removeEventListener("keydown", onEsc);
  }, [open, onClose]);

  if (!open) return null;

  return (
    <>
      <div className="mv2-fdr-scrim" onClick={onClose} />
      <div className="mv2-fdr">
        <div className="mv2-fdr-header">
          <span className="mv2-fdr-title">Filter</span>
          <button className="mv2-fdr-reset" onClick={handleReset}>Reset</button>
        </div>

        {/* Saved filter templates */}
        <div className="mv2-fdr-saved-wrap">
          <SavedFiltersPanel
            module="defects"
            appliedFilters={draft}
            filterMode={mode}
            onApply={(filterDef, fm) => {
              setDraft(JSON.parse(JSON.stringify(filterDef)));
              setMode(fm || "all");
              onApplyTemplate?.(filterDef, fm || "all");
              onApply(filterDef, fm || "all");
              onClose();
            }}
          />
        </div>

        <div className="mv2-fdr-search-wrap">
          <IconSearch />
          <input
            className="mv2-fdr-search"
            type="text"
            placeholder="Filter Search"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>

        <div className="mv2-fdr-list">
          {visibleFields.map((field) => {
            const isOpen = expanded === field.key;
            const curr = field.type === "daterange"
              ? (draft[field.key] || { from: "", to: "" })
              : field.type === "text"
                ? (draft[field.key] || { value: "" })
                : (draft[field.key] || { op: "is", values: [] });
            const fs = fieldSearch[field.key] || "";
            // Alphabetical (field.options already sorted this way) with
            // currently-selected values pinned to the top — recomputed live
            // off curr.values so checking a box moves it up immediately.
            // No-op for daterange/text fields (field.options is always []
            // for those, curr.values is undefined).
            const opts = (fs.trim()
              ? field.options.filter((o) => o.label.toLowerCase().includes(fs.toLowerCase()))
              : field.options
            ).slice().sort((a, b) => {
              const aSel = curr.values?.includes(a.value);
              const bSel = curr.values?.includes(b.value);
              if (aSel !== bSel) return aSel ? -1 : 1;
              return a.label.localeCompare(b.label);
            });
            const hasActive = field.type === "daterange"
              ? !!(curr.from || curr.to)
              : field.type === "text"
                ? !!curr.value?.trim()
                : curr.values?.length > 0;

            return (
              <div
                key={field.key}
                className={`mv2-fdr-field${isOpen ? " mv2-fdr-field--open" : ""}${hasActive ? " mv2-fdr-field--active" : ""}`}
              >
                <button
                  className="mv2-fdr-field-hdr"
                  onClick={() => setExpanded(isOpen ? null : field.key)}
                >
                  <span className="mv2-fdr-field-name">
                    {field.label}
                    {hasActive && (
                      <span className="mv2-fdr-badge">
                        {field.type === "daterange" || field.type === "text" ? "✓" : curr.values.length}
                      </span>
                    )}
                  </span>
                  <svg viewBox="0 0 24 24" width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                    {isOpen
                      ? <polyline points="18 15 12 9 6 15" />
                      : <polyline points="9 18 15 12 9 6" />
                    }
                  </svg>
                </button>

                {isOpen && (
                  <div className="mv2-fdr-field-body">
                    {hasActive && field.type !== "choice" && (
                      <div className="mv2-fdr-field-actions">
                        <button
                          type="button"
                          className="mv2-fdr-field-reset"
                          onClick={() => clearField(field.key)}
                        >
                          Reset this filter
                        </button>
                      </div>
                    )}
                    {field.type === "text" ? (
                      <div className="mv2-fdr-text-filter">
                        <div className="mv2-fdr-op-label">Contains</div>
                        <div className="mv2-fdr-val-search-wrap">
                          <IconSearch />
                          <input
                            className="mv2-fdr-val-search"
                            type="text"
                            placeholder={`Search ${field.label}...`}
                            value={curr.value || ""}
                            onChange={(e) => setDraft((d) => ({ ...d, [field.key]: { type: "text", value: e.target.value } }))}
                          />
                          {curr.value && (
                            <button
                              className="mv2-fdr-text-clear"
                              onClick={() => setDraft((d) => ({ ...d, [field.key]: { type: "text", value: "" } }))}
                            >×</button>
                          )}
                        </div>
                      </div>
                    ) : field.type === "daterange" ? (
                      <div className="mv2-fdr-daterange">
                        <div className="mv2-fdr-daterange-row">
                          <label className="mv2-fdr-daterange-label">From</label>
                          <input
                            type="date"
                            className="mv2-fdr-date-input"
                            value={curr.from || ""}
                            max={curr.to || undefined}
                            onChange={(e) => setDraft((d) => ({ ...d, [field.key]: { type: "daterange", ...d[field.key], from: e.target.value } }))}
                          />
                        </div>
                        <div className="mv2-fdr-daterange-row">
                          <label className="mv2-fdr-daterange-label">To</label>
                          <input
                            type="date"
                            className="mv2-fdr-date-input"
                            value={curr.to || ""}
                            min={curr.from || undefined}
                            onChange={(e) => setDraft((d) => ({ ...d, [field.key]: { type: "daterange", ...d[field.key], to: e.target.value } }))}
                          />
                        </div>
                        {(curr.from || curr.to) && (
                          <button
                            className="mv2-fdr-daterange-clear"
                            onClick={() => setDraft((d) => ({ ...d, [field.key]: { type: "daterange", from: "", to: "" } }))}
                          >
                            Clear dates
                          </button>
                        )}
                      </div>
                    ) : field.key === "tags" ? (
                      <>
                        <div className="mv2-fdr-op-row">
                          <select
                            className="mv2-fdr-op"
                            value={curr.op}
                            onChange={(e) => setOp(field.key, e.target.value)}
                          >
                            <option value="is">Is</option>
                            <option value="is_not">Is Not</option>
                          </select>
                          {hasActive && (
                            <button
                              type="button"
                              className="mv2-fdr-field-reset"
                              onClick={() => clearField(field.key)}
                            >
                              Reset this filter
                            </button>
                          )}
                        </div>

                        {curr.values.length > 0 && (
                          <div className="mv2-fdr-tag-chips">
                            {curr.values.map((v) => (
                              <span key={v} className="mv2-fdr-tag-chip">
                                {v}
                                <button
                                  type="button"
                                  className="mv2-fdr-tag-chip-x"
                                  onClick={() => toggleVal(field.key, v)}
                                >×</button>
                              </span>
                            ))}
                          </div>
                        )}

                        <div className="mv2-fdr-val-search-wrap">
                          <IconSearch />
                          <input
                            className="mv2-fdr-val-search"
                            type="text"
                            placeholder="Search tags…"
                            value={tagQuery}
                            onChange={(e) => setTagQuery(e.target.value)}
                          />
                        </div>

                        <div className="mv2-fdr-opts">
                          {tagLoadingSuggest && (
                            <div className="mv2-fdr-empty">Searching…</div>
                          )}
                          {!tagLoadingSuggest && tagQuery.trim() && tagSuggestions.filter((t) => !curr.values.includes(t.tag_name)).length === 0 && (
                            <div className="mv2-fdr-empty">No tags found</div>
                          )}
                          {!tagLoadingSuggest && !tagQuery.trim() && curr.values.length === 0 && (
                            <div className="mv2-fdr-empty">Type to search tags</div>
                          )}
                          {tagSuggestions
                            .filter((t) => !curr.values.includes(t.tag_name))
                            .map((t) => (
                              <label key={t.id} className="mv2-fdr-opt">
                                <input
                                  type="checkbox"
                                  checked={false}
                                  onChange={() => {
                                    toggleVal(field.key, t.tag_name);
                                    setTagQuery("");
                                    setTagSuggestions([]);
                                  }}
                                />
                                <span>{t.tag_name}</span>
                              </label>
                            ))}
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="mv2-fdr-op-row">
                          <select
                            className="mv2-fdr-op"
                            value={curr.op}
                            onChange={(e) => setOp(field.key, e.target.value)}
                          >
                            <option value="is">Is</option>
                            <option value="is_not">Is Not</option>
                          </select>
                          {hasActive && (
                            <button
                              type="button"
                              className="mv2-fdr-field-reset"
                              onClick={() => clearField(field.key)}
                            >
                              Reset this filter
                            </button>
                          )}
                        </div>

                        {field.options.length > 5 && (
                          <div className="mv2-fdr-val-search-wrap">
                            <IconSearch />
                            <input
                              className="mv2-fdr-val-search"
                              type="text"
                              placeholder="Search..."
                              value={fs}
                              onChange={(e) => setFieldSearch((s) => ({ ...s, [field.key]: e.target.value }))}
                            />
                          </div>
                        )}

                        <div className="mv2-fdr-opts">
                          {opts.map((opt) => (
                            <label key={opt.value} className="mv2-fdr-opt">
                              <input
                                type="checkbox"
                                checked={curr.values.includes(opt.value)}
                                onChange={() => toggleVal(field.key, opt.value)}
                              />
                              <span>{opt.label}</span>
                            </label>
                          ))}
                          {opts.length === 0 && (
                            <div className="mv2-fdr-empty">No options</div>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>

        <div className="mv2-fdr-mode">
          <label className="mv2-fdr-mode-opt">
            <input
              type="radio"
              name="dfv2FdrMode"
              value="any"
              checked={mode === "any"}
              onChange={() => setMode("any")}
            />
            Any of these
          </label>
          <label className="mv2-fdr-mode-opt">
            <input
              type="radio"
              name="dfv2FdrMode"
              value="all"
              checked={mode === "all"}
              onChange={() => setMode("all")}
            />
            All of these
          </label>
        </div>

        <div className="mv2-fdr-footer">
          <button className="mv2-fdr-find" onClick={handleApply}>Find</button>
          <button className="mv2-fdr-cancel" onClick={onClose}>Cancel</button>
        </div>
      </div>
    </>
  );
}

function IssuesTable({
  defects,
  user,
  loading,
  visibleColumns,
  onEditDefect,
  onStatusChange,
}) {
  const currentUserName = user?.name || user?.preferred_username || "";
  const visibleSet = useMemo(() => new Set(visibleColumns || []), [visibleColumns]);
  const hasCol = (k) => visibleSet.has(k);
  const wrapRef = useRef(null);
  const [wrapWidth, setWrapWidth] = useState(0);

  useEffect(() => {
    if (!wrapRef.current) return;
    const el = wrapRef.current;
    const update = () => setWrapWidth(el.clientWidth || 0);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const colWidths = useMemo(() => {
    const base = {
      id: 116,
      title: 220,
      severity: 80,
      priority: 80,
      type: 110,
      status: 116,
      environment: 64,
      visibility: 88,
      source: 88,
      reporting_channel: 140,
      reporter: 150,
      owner: 150,
      team: 130,
      project: 116,
      customer: 132,
      aging: 92,
      created: 112,
      updated: 112,
      due: 72,
      closed: 72,
    };

    const growthCap = {
      title: 320,
      type: 80,
      reporter: 120,
      owner: 120,
      team: 100,
      project: 80,
      customer: 80,
    };

    const optionalKeys = Object.keys(base).filter(k => !["id", "title"].includes(k));
    const isVisible = (k) => visibleSet.has(k);
    const baseTotal = base.id + base.title + optionalKeys.reduce((sum, k) => sum + (isVisible(k) ? base[k] : 0), 0);
    let remaining = Math.max(0, wrapWidth - baseTotal);
    const widths = { ...base };

    ["title", "type", "reporter", "owner", "team", "project", "customer"].forEach((k) => {
      if (k !== "title" && !isVisible(k)) return;
      const cap = growthCap[k] || 0;
      const add = Math.min(cap, remaining);
      widths[k] += add;
      remaining -= add;
    });

    if (remaining > 0) widths.title += remaining;

    const computedTotal =
      widths.id +
      widths.title +
      optionalKeys.reduce((sum, k) => sum + (isVisible(k) ? widths[k] : 0), 0);

    return { widths, minTotalWidth: computedTotal };
  }, [wrapWidth, visibleSet]);

  return (
    <>
      <div className="page-table-wrap" ref={wrapRef}>
        {loading && <div className="dfv2-loading">Loading defects…</div>}
        {!loading && defects.length === 0 && (
          <div className="dfv2-empty">No defects match the current filters.</div>
        )}
        {!loading && defects.length > 0 && (
          <table className="page-table" style={{ minWidth: `${colWidths.minTotalWidth}px` }}>
            <colgroup>
              <col className="c-id" style={{ width: `${colWidths.widths.id}px` }} />
              <col className="c-title" style={{ width: `${colWidths.widths.title}px` }} />
              {hasCol("severity") && <col className="c-sev" style={{ width: `${colWidths.widths.severity}px` }} />}
              {hasCol("priority") && <col className="c-pri" style={{ width: `${colWidths.widths.priority}px` }} />}
              {hasCol("type") && <col className="c-type" style={{ width: `${colWidths.widths.type}px` }} />}
              {hasCol("status") && <col className="c-status" style={{ width: `${colWidths.widths.status}px` }} />}
              {hasCol("environment") && <col className="c-env" style={{ width: `${colWidths.widths.environment}px` }} />}
              {hasCol("visibility") && <col className="c-vis" style={{ width: `${colWidths.widths.visibility}px` }} />}
              {hasCol("source") && <col className="c-source" style={{ width: `${colWidths.widths.source}px` }} />}
              {hasCol("reporting_channel") && <col className="c-rc" style={{ width: `${colWidths.widths.reporting_channel}px` }} />}
              {hasCol("reporter") && <col className="c-repo" style={{ width: `${colWidths.widths.reporter}px` }} />}
              {hasCol("owner") && <col className="c-owner" style={{ width: `${colWidths.widths.owner}px` }} />}
              {hasCol("team") && <col className="c-team" style={{ width: `${colWidths.widths.team}px` }} />}
              {hasCol("project") && <col className="c-proj" style={{ width: `${colWidths.widths.project}px` }} />}
              {hasCol("customer") && <col className="c-cust" style={{ width: `${colWidths.widths.customer}px` }} />}
              {hasCol("aging") && <col className="c-age" style={{ width: `${colWidths.widths.aging}px` }} />}
              {hasCol("created") && <col className="c-created" style={{ width: `${colWidths.widths.created}px` }} />}
              {hasCol("updated") && <col className="c-updated" style={{ width: `${colWidths.widths.updated}px` }} />}
              {hasCol("due") && <col className="c-due" style={{ width: `${colWidths.widths.due}px` }} />}
              {hasCol("closed") && <col className="c-closed" style={{ width: `${colWidths.widths.closed}px` }} />}
            </colgroup>
            <thead>
              <tr>
                <th>ID</th>
                <th>Title</th>
                {hasCol("severity") && <th>Severity</th>}
                {hasCol("priority") && <th>Priority</th>}
                {hasCol("type") && <th>Type</th>}
                {hasCol("status") && <th>Status</th>}
                {hasCol("environment") && <th>Env</th>}
                {hasCol("visibility") && <th>Visibility</th>}
                {hasCol("source") && <th>Source</th>}
                {hasCol("reporting_channel") && <th>Reporting Channel</th>}
                {hasCol("reporter") && <th>Reporter</th>}
                {hasCol("owner") && <th>Owner</th>}
                {hasCol("team") && <th>Team</th>}
                {hasCol("project") && <th>Project</th>}
                {hasCol("customer") && <th>Customer</th>}
                {hasCol("aging") && <th>Aging</th>}
                {hasCol("created") && <th>Created</th>}
                {hasCol("updated") && <th>Updated</th>}
                {hasCol("due") && <th>Due</th>}
                {hasCol("closed") && <th>Closed</th>}
              </tr>
            </thead>
            <tbody>
              {defects.map((d, i) => {
                const days = ageDays(d.created_at);
                const isMe = !!(currentUserName && (d.assigned_to_name || "").toLowerCase().includes(currentUserName.split("@")[0].toLowerCase()));
                const handleEditClick = (e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditDefect?.(d);
                };
                return (
                  <tr key={d.id}>
                    <td className="id">{defectId(d)}</td>
                    <td className="title">
                      <button
                        type="button"
                        className="dfv2-title-edit-btn"
                        title="Edit defect"
                        onClick={handleEditClick}
                      >
                        <span className="tt-lead" title={d.title || ""}>{d.title || "Untitled"}</span>
                      </button>
                    </td>
                    {hasCol("severity") && <td><SevPip severity={d.severity} /></td>}
                    {hasCol("priority") && <td><SevPip severity={d.priority} /></td>}
                    {hasCol("type") && (
                      <td className="tt-type">
                        {d.defect_type && <span className="tt-type-main">{d.defect_type}</span>}
                        {d.build_version && <span className="tt-type-sub">{d.build_version}</span>}
                      </td>
                    )}
                    {hasCol("status") && (
                      <td>
                        {(ALLOWED_TRANSITIONS[d.status] || []).length > 0 ? (
                          // Only wrap in the double-click-to-edit cell when
                          // there's actually somewhere to transition to —
                          // a terminal status (e.g. Closed) stays a plain
                          // pill, since an empty dropdown would be
                          // confusing and InlineCell falls back to a free-
                          // text input (wrong for a status field) when
                          // `options` is falsy/empty.
                          <InlineCell
                            value={d.status}
                            field="status"
                            options={ALLOWED_TRANSITIONS[d.status]}
                            onCommit={(_field, toStatus) => onStatusChange?.(d, toStatus)}
                          >
                            <StPill status={d.status} />
                          </InlineCell>
                        ) : (
                          <StPill status={d.status} />
                        )}
                      </td>
                    )}
                    {hasCol("environment") && <td><EnvChip env={d.environment} /></td>}
                    {hasCol("visibility") && <td><VisPill visibility={d.visibility} /></td>}
                    {hasCol("source") && <td>{d.source || "—"}</td>}
                    {hasCol("reporting_channel") && <td>{d.reporting_channel || "—"}</td>}
                    {hasCol("reporter") && <td><PersonCell name={d.reported_by_name || d.reported_by || "—"} /></td>}
                    {hasCol("owner") && <td><PersonCell name={d.assigned_to_name || d.assigned_to || "—"} isMe={isMe} /></td>}
                    {hasCol("team") && <td>{d.assigned_to_team_names || "—"}</td>}
                    {hasCol("project") && <td><div className="dfv2-proj">{d.project_name || "—"}</div></td>}
                    {hasCol("customer") && <td><div className="dfv2-proj">{d.customer_name || "—"}</div></td>}
                    {hasCol("aging") && <td><AgingCell days={days} /></td>}
                    {hasCol("created") && <td className="td-date">{fmtDateTime(d.created_at)}</td>}
                    {hasCol("updated") && <td className="td-date td-upd">{fmtDateTime(d.updated_at)}</td>}
                    {hasCol("due") && (
                      <td className={`td-date${d.due_date && d.status !== "Closed" ? (() => { const today = new Date().toISOString().slice(0, 10); const due = (d.due_date || "").slice(0, 10); return due < today ? " due-past" : due === today ? " due-today" : ""; })() : ""}`}>
                        {d.due_date ? fmtDate(d.due_date) : <span className="td-date-empty">—</span>}
                      </td>
                    )}
                    {hasCol("closed") && (
                      <td className="td-dates">
                        {d.status === "Closed" ? fmtDate(d.closed_at) : <span className="td-date-empty">—</span>}
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </>
  );
}

function DefectsBoard({
  defects,
  onEditDefect,
  collapsedCols,
  setCollapsedCols,
  boardCols,
  draggingDefect,
  dragOverStatus,
  onDragStartDefect,
  onDragOverStatus,
  onDropStatus,
  onDragEndDefect,
}) {
  const [dragBlockedCol, setDragBlockedCol] = useState(null);

  const dueClass = useCallback((defect) => {
    if (!defect?.due_date || defect?.status === "Closed" || defect?.status === "Rejected") return "";
    const due = new Date(defect.due_date);
    if (Number.isNaN(due.getTime())) return "";
    const dueDay = new Date(due);
    dueDay.setHours(0, 0, 0, 0);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    if (dueDay.getTime() < today.getTime()) return " mv2-board-card-due--overdue";
    if (dueDay.getTime() === today.getTime()) return " mv2-board-card-due--today";
    return " mv2-board-card-due--future";
  }, []);

  return (
    <div className="mv2-board">
      {boardCols.map((col) => {
        const colDefects = defects.filter((d) => (d.status || "Open") === col);
        const isCollapsed = collapsedCols.has(col);
        const toggleCollapse = (e) => {
          e?.stopPropagation?.();
          setCollapsedCols((prev) => {
            const next = new Set(prev);
            if (next.has(col)) next.delete(col);
            else next.add(col);
            return next;
          });
        };

        const canDrop = !draggingDefect || (ALLOWED_TRANSITIONS[draggingDefect.status] || []).includes(col);
        const isOver = dragOverStatus === col;
        const isBlocked = dragBlockedCol === col;

        if (isCollapsed) {
          return (
            <div
              key={col}
              className={`mv2-board-col mv2-board-col--collapsed${isOver ? " mv2-board-col--over" : ""}${isBlocked ? " mv2-board-col--blocked" : ""}`}
              onClick={toggleCollapse}
              title={`${col} (${colDefects.length}) — click to expand`}
              onDragOver={(e) => { if (canDrop) { onDragOverStatus?.(e, col); setDragBlockedCol(null); } else { setDragBlockedCol(col); } }}
              onDrop={() => { if (canDrop) onDropStatus?.(col); setDragBlockedCol(null); }}
              onDragLeave={() => { onDragOverStatus?.(null, null); setDragBlockedCol(null); }}
            >
              <div className="mv2-board-col-collapsed-inner">
                <span className="mv2-board-col-collapsed-label">
                  {col} ({colDefects.length})
                </span>
                <span
                  className="mv2-status-dot"
                  style={{
                    width: 7,
                    height: 7,
                    background: DEFECT_STATUS_COLOR[col] || "#AFB2BA",
                  }}
                />
              </div>
            </div>
          );
        }

        return (
          <div
            key={col}
            className={`mv2-board-col${isOver ? " mv2-board-col--over" : ""}${isBlocked ? " mv2-board-col--blocked" : ""}`}
            onDragOver={(e) => { if (canDrop) { onDragOverStatus?.(e, col); setDragBlockedCol(null); } else { setDragBlockedCol(col); } }}
            onDrop={() => { if (canDrop) onDropStatus?.(col); setDragBlockedCol(null); }}
            onDragLeave={() => { onDragOverStatus?.(null, null); setDragBlockedCol(null); }}
          >
            <div className="mv2-board-col-header">
              <span
                className="mv2-status-dot"
                style={{
                  width: 8,
                  height: 8,
                  background: DEFECT_STATUS_COLOR[col] || "#AFB2BA",
                }}
              />
              <span className="mv2-board-col-label">{col}</span>
              <span className="mv2-board-col-count">{colDefects.length}</span>
              <button
                className="mv2-board-collapse-btn"
                title="Collapse column"
                onClick={toggleCollapse}
              >
                <svg viewBox="0 0 24 24" width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
                  <polyline points="15 18 9 12 15 6" />
                </svg>
              </button>
            </div>
            <div className="mv2-board-cards">
              {colDefects.map((d) => {
                const assignee = d.assigned_to_name || d.assigned_to || "";
                return (
                  <div
                    key={d.id}
                    className={`mv2-board-card${draggingDefect?.id === d.id ? " mv2-board-card--dragging" : ""}`}
                    draggable
                    onDragStart={() => onDragStartDefect?.(d)}
                    onDragEnd={() => onDragEndDefect?.()}
                    style={{ cursor: "pointer" }}
                    onClick={() => onEditDefect?.(d)}
                    title={d.title || "Untitled defect"}
                  >
                    <div className="mv2-board-card-header">
                      <span className={`mv2-board-card-due${dueClass(d)}`}>
                        {d.due_date ? fmtDate(d.due_date) : "No due date"}
                      </span>
                      <span className="mv2-board-card-effort">{d.severity || "—"}</span>
                    </div>
                    <div className="mv2-board-card-desc">{d.title || "Untitled defect"}</div>
                    <div className="mv2-board-card-meta">
                      <span className="mv2-board-card-id">{defectId(d)}</span>
                      {d.customer_name && <span className="mv2-board-card-customer">{d.customer_name}</span>}
                      <span className="mv2-board-card-assignee" title={assignee || "Unassigned"}>
                        {initials(assignee || "?")}
                      </span>
                    </div>
                  </div>
                );
              })}
              {colDefects.length === 0 && (
                <div className="dfv2-empty" style={{ padding: "14px 12px", textAlign: "center" }}>No defects</div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Sticky Footer ──────────────────────────────────────────────────────────────
function FooterBar({ open, total, activeFilters, lastUpdated }) {
  return (
    <div className="dfv2-footbar">
      <span className="fb-item">
        <span className="k">Total</span>
        <b>{total}</b>
      </span>
      <span className="fb-item">
        <span className="k">Open</span>
        <b>{open}</b>
      </span>
      {activeFilters.length > 0 && (
        <span className="fb-item">
          <span className="k">Filters</span>
          <b>{activeFilters.join(", ")}</b>
        </span>
      )}
      <span className="fb-spacer" />
      <span className="fb-dot" />
      <span style={{ color: "rgba(255,255,255,0.5)", letterSpacing: "0.02em", textTransform: "none" }}>
        Updated {lastUpdated}
      </span>
    </div>
  );
}

// ── Searchable single-select combobox ─────────────────────────────────────────
export default function DefectsViewV2({ showToast, user }) {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(
    () => localStorage.getItem("sb-collapsed") === "true"
  );
  const [viewTab,    setViewTab]    = useState("dashboard");
  const [issuesView, setIssuesView] = useState("table");
  const [execSummary,        setExecSummary]        = useState(null);
  const [execSummaryLoading, setExecSummaryLoading] = useState(false);
  const execSummaryFetchedRef = useRef(false);
  const [collapsedCols, setCollapsedCols] = useState(new Set());
  const [range,      setRange]      = useState("30d");
  const [customRange, setCustomRange] = useState({ from: "", to: "" });
  const [customDraft, setCustomDraft] = useState({ from: "", to: "" });
  const [customOpen,  setCustomOpen]  = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [editDrawerDefect, setEditDrawerDefect] = useState(null);
  const [editDrawerInitialTab, setEditDrawerInitialTab] = useState("overview");
  const [draggingDefect, setDraggingDefect] = useState(null);
  const [dragOverStatus, setDragOverStatus] = useState(null);
  // Issues table's inline status edit (double-click the Status pill) — holds
  // the pending change while StatusTransitionReasonModal collects a reason,
  // for the two statuses that require one.
  const [pendingStatusChange, setPendingStatusChange] = useState(null); // { defect, toStatus }
  const searchRef = useRef(null);
  const [search,     setSearch]     = useState("");
  // Debounced counterpart of `search` sent to the server (see useDefectsData/
  // buildDefectsQueryParams) — avoids an API call on every keystroke, same
  // pattern/interval as TasksViewV2Page.jsx's debouncedSearch. `search` itself
  // still drives the input value and the instant client-side narrowing in
  // useDefectsViewDerived; debouncedSearch only widens what's actually
  // fetched so that narrowing has the full matching set to work with instead
  // of just whatever page happened to already be loaded.
  const [debouncedSearch, setDebouncedSearch] = useState("");
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(search), 400);
    return () => clearTimeout(t);
  }, [search]);
  const [appliedFilters, setAppliedFilters] = useState(DEFAULT_DEFECT_APPLIED_FILTERS);
  const [filterMode, setFilterMode] = useState("all");
  const [filterOpen, setFilterOpen] = useState(false);

  // Auto-apply default filter template on mount
  useEffect(() => {
    listFilterTemplates("defects").then((templates) => {
      const def = (templates || []).find((t) => t.is_default);
      if (def) {
        setAppliedFilters(def.filter_def || {});
        setFilterMode(def.filter_mode || "all");
      }
    }).catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps
  const [lcSelected, setLcSelected] = useState(null);
  const [teams, setTeams] = useState([]);
  const [pageSize, setPageSize] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const allOptionalColumnKeys = useMemo(() => ISSUE_OPTIONAL_COLUMNS.map(c => c.key), []);
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const fallback = ISSUE_OPTIONAL_COLUMNS.map(c => c.key);
    try {
      const raw = localStorage.getItem("dfv2-visible-columns");
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      if (!Array.isArray(parsed)) return fallback;
      const valid = parsed.filter(k => fallback.includes(k));
      return valid.length ? valid : fallback;
    } catch {
      return fallback;
    }
  });
  const [columnPickerOpen, setColumnPickerOpen] = useState(false);
  const columnPickerRef = useRef(null);
  const deepLinkHandledRef = useRef(false);

  useEffect(() => {
    let cancelled = false;
    defectsApi.listTeams()
      .then((data) => {
        if (cancelled) return;
        setTeams(Array.isArray(data) ? data : []);
      })
      .catch(() => {
        if (!cancelled) setTeams([]);
      });
    return () => { cancelled = true; };
  }, []);

  const teamFilters = useMemo(() => {
    const reported = appliedFilters?.reported_team_id;
    const owning = appliedFilters?.owning_team_id;
    return {
      reportedTeamIds: Array.isArray(reported?.values) ? reported.values.filter(Boolean) : [],
      reportedTeamOp: reported?.op || "is",
      owningTeamIds: Array.isArray(owning?.values) ? owning.values.filter(Boolean) : [],
      owningTeamOp: owning?.op || "is",
    };
  }, [appliedFilters]);

  // Prevent browser back/forward swipe gesture while on this screen
  useEffect(() => {
    const html = document.documentElement;
    const body = document.body;
    const prevHtmlOverscrollX = html.style.overscrollBehaviorX;
    const prevBodyOverscrollX = body.style.overscrollBehaviorX;

    html.style.overscrollBehaviorX = "none";
    body.style.overscrollBehaviorX = "none";

    return () => {
      html.style.overscrollBehaviorX = prevHtmlOverscrollX;
      body.style.overscrollBehaviorX = prevBodyOverscrollX;
    };
  }, []);

  useEffect(() => {
    localStorage.setItem("dfv2-visible-columns", JSON.stringify(visibleColumns));
  }, [visibleColumns]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (!columnPickerRef.current) return;
      if (!columnPickerRef.current.contains(e.target)) {
        setColumnPickerOpen(false);
      }
    };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, []);

  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        if (viewTab !== "issues" && viewTab !== "mine" && viewTab !== "external") return;
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [viewTab]);

  const {
    defects,
    setDefects,
    optionsDefects,
    listTotal,
    knownCounts,
    tabCounts,
    statusSummary,
    statusSummaryLoading,
    loading,
    lastUpdated,
    refreshIssueCounts,
    fetchDefectsForRange,
  } = useDefectsData({
    viewTab,
    range,
    customRange,
    userId: user?.db_user_id,
    showToast,
    teamFilters,
    appliedFilters,
    search: debouncedSearch,
  });

  // Client-side visibility guard (server already enforces this; belt-and-suspenders)
  const visibleDefects = useMemo(
    () => (Array.isArray(defects) ? defects.filter(d => canViewDefectByVisibility(d, user)) : []),
    [defects, user]
  );

  // Same guard applied to the separate, filter-independent dataset used to
  // build the dynamic choice-filter option lists (see useDefectsData.js).
  const visibleOptionsDefects = useMemo(
    () => (Array.isArray(optionsDefects) ? optionsDefects.filter(d => canViewDefectByVisibility(d, user)) : []),
    [optionsDefects, user]
  );

  const {
    rangeDefects,
    comboData,
    rangeStats,
    tableDefects,
    boardColumns,
    activeFilters,
    activeFilterCount,
  } = useDefectsViewDerived({
    defects: visibleDefects,
    range,
    customRange,
    lcSelected,
    search,
    appliedFilters,
    filterMode,
    viewTab,
    user,
  });

  const totalRows = tableDefects.length;
  const totalPages = pageSize === "all" ? 1 : Math.max(1, Math.ceil(totalRows / Number(pageSize)));
  const pageStartIdx = pageSize === "all" ? 0 : (currentPage - 1) * Number(pageSize);
  const paginatedDefects = pageSize === "all"
    ? tableDefects
    : tableDefects.slice(pageStartIdx, pageStartIdx + Number(pageSize));

  const liveTabCounts = useMemo(() => {
    if (viewTab !== "issues" && viewTab !== "mine" && viewTab !== "external") return tabCounts;
    return { ...tabCounts, ...knownCounts };
  }, [viewTab, tabCounts, knownCounts]);

  useEffect(() => {
    if (deepLinkHandledRef.current) return;
    const params = new URLSearchParams(window.location.search);
    const openDefectId = params.get("openDefectId") || params.get("defectId");
    if (!openDefectId) {
      deepLinkHandledRef.current = true;
      return;
    }
    deepLinkHandledRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const defect = await defectsApi.getById(openDefectId);
        if (cancelled) return;
        if (defect?.id) {
          setViewTab("issues");
          setIssuesView("table");
          setEditDrawerInitialTab("overview");
          setEditDrawerDefect(defect);
        } else {
          showToast("Defect not found for deep link", "warn");
        }
      } catch {
        if (!cancelled) showToast("Defect not found for deep link", "warn");
      } finally {
        params.delete("openDefectId");
        params.delete("defectId");
        const nextQs = params.toString();
        const nextUrl = `${window.location.pathname}${nextQs ? `?${nextQs}` : ""}`;
        window.history.replaceState({}, "", nextUrl);
      }
    })();
    return () => { cancelled = true; };
    // Intentionally run once on mount only (deepLinkHandledRef already makes
    // this idempotent) — depending on `showToast` let a parent re-render
    // mid-fetch re-run this effect, whose cleanup set `cancelled = true` and
    // silently dropped the setEditDrawerDefect() call on a successful fetch.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (viewTab !== "executive-summary" || execSummaryFetchedRef.current) return;
    // No cancelled-flag/cleanup here on purpose — this is a simple, idempotent
    // GET with no side effects, and StrictMode's dev-only double-invoke of
    // effects (mount -> cleanup -> mount) would otherwise race a cleanup-set
    // `cancelled = true` against this same fetch's own resolution, permanently
    // stranding execSummaryLoading at true with no data ever rendered (this
    // shipped once already — the effect used to depend on execSummaryLoading,
    // state it itself sets, compounding the race). The ref persists across
    // StrictMode's synchronous double-invoke, so only the first invocation's
    // fetch ever actually runs.
    execSummaryFetchedRef.current = true;
    setExecSummaryLoading(true);
    defectsApi.executiveSummary()
      .then(setExecSummary)
      .catch(() => {
        showToast("Failed to load Executive Summary", "warn");
        execSummaryFetchedRef.current = false; // allow retry next time the tab is opened
      })
      .finally(() => setExecSummaryLoading(false));
  }, [viewTab, showToast]);

  useEffect(() => {
    setCurrentPage(1);
  }, [viewTab, search, appliedFilters, filterMode, lcSelected]);

  useEffect(() => {
    if (pageSize === "all") {
      setCurrentPage(1);
      return;
    }
    setCurrentPage(p => Math.min(p, totalPages));
  }, [pageSize, totalPages]);

  // Range label for KPI
  const rangeLabel = "All";

  // Handle lifecycle bar selection
  const handleLcSelect = useCallback((status) => {
    if (status && viewTab === "dashboard") {
      // Mirror the SAME window + filter shape GET /defects/status-counts used
      // for this segment's own count — activity_window (created OR closed in
      // range), not created_at, so the Issues tab's result set is guaranteed
      // to match what the Lifecycle bar showed. See defectsQuery.js's
      // applyDateRangeFilter(appliedFilters?.activity_window, ...) and
      // routers/defects.py's activity_from/activity_to docstring.
      const { from, to } = computeRangeDates(range, customRange);
      setAppliedFilters((prev) => ({
        ...prev,
        activity_window: { type: "daterange", from, to },
        status: { op: "is", values: [status] },
      }));
      setLcSelected(null);
      setViewTab("issues");
    } else {
      setLcSelected(status);
    }
  }, [viewTab, range, customRange]);

  const handleBoardDragStart = useCallback((defect) => {
    setDraggingDefect(defect);
  }, []);

  const handleBoardDragOver = useCallback((e, status) => {
    if (e?.preventDefault) e.preventDefault();
    setDragOverStatus(status || null);
  }, []);

  const handleBoardDragEnd = useCallback(() => {
    setDraggingDefect(null);
    setDragOverStatus(null);
  }, []);

  const handleBoardDrop = useCallback(async (status) => {
    const moving = draggingDefect;
    setDraggingDefect(null);
    setDragOverStatus(null);

    if (!moving || !status || moving.status === status) return;

    const allowed = ALLOWED_TRANSITIONS[moving.status] || [];
    if (!allowed.includes(status)) {
      const allowedList = allowed.length ? allowed.join(", ") : "none";
      showToast?.(`"${moving.status}" → "${status}" is not a valid transition. Allowed: ${allowedList}.`, "error");
      return;
    }

    setDefects((prev) => prev.map((d) => (d.id === moving.id ? { ...d, status } : d)));

    try {
      await defectsApi.updateStatus(moving.id, status);
      refreshIssueCounts();
    } catch (err) {
      setDefects((prev) => prev.map((d) => (d.id === moving.id ? { ...d, status: moving.status } : d)));
      showToast?.(err.message || "Failed to update defect status", "error");
    }
  }, [draggingDefect, setDefects, showToast, refreshIssueCounts]);

  // Issues table's inline status edit (InlineCell, double-click the Status
  // pill). Uses POST /defects/{id}/transition, not the generic PATCH the
  // Kanban board's own handleBoardDrop above uses — see defectsApi.js's
  // transitionStatus() comment for why. Server-side validates the move
  // (409 if illegal) even though the dropdown's own options are already
  // capped to ALLOWED_TRANSITIONS[current], so this is defense-in-depth,
  // not the primary guard.
  const commitDefectStatusChange = useCallback(async (defect, toStatus, reason) => {
    const fromStatus = defect.status;
    setDefects((prev) => prev.map((d) => (d.id === defect.id ? { ...d, status: toStatus } : d)));
    try {
      await defectsApi.transitionStatus(defect.id, toStatus, reason);
      refreshIssueCounts();
    } catch (err) {
      setDefects((prev) => prev.map((d) => (d.id === defect.id ? { ...d, status: fromStatus } : d)));
      showToast?.(err.message || "Failed to update defect status", "error");
    }
  }, [setDefects, showToast, refreshIssueCounts]);

  const handleInlineStatusChange = useCallback((defect, toStatus) => {
    if (!toStatus || toStatus === defect.status) return;
    if (STATUS_REASON_REQUIRED.has(toStatus)) {
      // Collect the mandatory reason first — commit happens from the
      // modal's onConfirm below, not here.
      setPendingStatusChange({ defect, toStatus });
      return;
    }
    commitDefectStatusChange(defect, toStatus, null);
  }, [commitDefectStatusChange]);

  // Server-side export: streams every defect matching the current filters
  // (not just the currently-loaded, /defects-default-capped page).
  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    const query = buildDefectsQueryParams({
      viewTab, range, customRange, userId: user?.db_user_id, teamFilters, appliedFilters,
    });
    if (!query || query.mode !== "list") return;
    setExporting(true);
    try {
      const csvText = await defectsApi.exportCsv(query.params);
      const blob = new Blob([csvText], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `defects_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      URL.revokeObjectURL(url);
    } catch (err) {
      showToast?.(err.message || "Failed to export defects", "error");
    } finally {
      setExporting(false);
    }
  }, [viewTab, range, customRange, user, teamFilters, appliedFilters, showToast]);

  return (
    <div
      className="dfv2-root"
      style={{ gridTemplateColumns: sidebarCollapsed ? `${SB_W_COLLAPSED} 1fr` : `${SB_W} 1fr` }}
    >
      <AppSidebar
        user={user}
        collapsed={sidebarCollapsed}
        onCollapse={() => setSidebarCollapsed(c => {
          const next = !c;
          localStorage.setItem("sb-collapsed", String(next));
          return next;
        })}
      />

      {/* ── Main content ── */}
    <div className="dfv2-shell">
      {/* Editorial header */}
      <header className="page-head">
        <h1>
          Issues{" "}
          <em>
            {statusSummaryLoading ? "loading…" : `${statusSummary.open} Open · ${statusSummary.critical_open} critical`}
          </em>
        </h1>
        <div className="page-head-actions">
          {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && (
            <div className="mv2-view-toggle">
              <button
                className={`mv2-view-btn${issuesView === "table" ? " mv2-view-btn--active" : ""}`}
                onClick={() => setIssuesView("table")}
                title="Table view"
              >
                <IconTable />
              </button>
              <button
                className={`mv2-view-btn${issuesView === "board" ? " mv2-view-btn--active" : ""}`}
                onClick={() => setIssuesView("board")}
                title="Board view"
              >
                <IconKanban />
              </button>
            </div>
          )}
          {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && (
            <button className="mv2-export-btn" onClick={handleExport} disabled={exporting}>
              <IconDownload />
              {exporting ? "Exporting…" : "Export"}
            </button>
          )}
          <button className="dfv2-new-btn v2-btn-primary" onClick={() => setDrawerOpen(true)}>
            <span className="plus">+</span>
            New defect
          </button>
        </div>
      </header>

      {/* View tabs */}
      <div className="page-toolbar">
        {[
          { key: "dashboard", label: "Dashboard" },
          ...(isAdminOrAmInternal(user) ? [{ key: "executive-summary", label: "Executive Summary" }] : []),
          { key: "issues",    label: "Issues",  count: liveTabCounts.issues },
          { key: "mine",      label: "My view", count: liveTabCounts.mine },
          { key: "external",  label: "External view", count: liveTabCounts.external },
        ].map(tab => (
          <button
            key={tab.key}
            className={`page-tab${viewTab === tab.key ? " page-tab--active" : ""}`}
            onClick={() => setViewTab(tab.key)}
          >
            {tab.label}
            {tab.count !== undefined && (
              <span className="page-tab-count">{tab.count}</span>
            )}
          </button>
        ))}
        {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && issuesView === "board" && (
          <button
            className="mv2-board-collapse-all-btn"
            title={boardColumns.every((c) => collapsedCols.has(c)) ? "Expand all columns" : "Collapse all columns"}
            onClick={() => {
              const allCollapsed = boardColumns.every((c) => collapsedCols.has(c));
              setCollapsedCols(allCollapsed ? new Set() : new Set(boardColumns));
            }}
          >
            {boardColumns.every((c) => collapsedCols.has(c)) ? "Expand All" : "Collapse All"}
          </button>
        )}
        <div className="page-toolbar-right">
          {viewTab === "dashboard" && (
            <div className="dfv2-range-seg">
              {["90", "30", "14", "7"].map(r => (
                <button
                  key={r}
                  className={range === `${r}d` ? "on" : ""}
                  onClick={() => { setRange(`${r}d`); setCustomOpen(false); }}
                >{r}D</button>
              ))}
              <div className="dfv2-custom-wrap">
                <button
                  className={range === "custom" ? "on" : ""}
                  onClick={() => {
                    setCustomDraft(customRange.from ? customRange : { from: "", to: "" });
                    setCustomOpen(o => !o);
                  }}
                >
                  Custom
                  {range === "custom" && customRange.from && customRange.to && (
                    <span className="dfv2-range-custom-badge">
                      {customRange.from.slice(5)} – {customRange.to.slice(5)}
                    </span>
                  )}
                </button>
                {customOpen && (
                  <div className="dfv2-custom-dropdown">
                    <div className="cd-title">Custom range</div>
                    <div className="cd-row">
                      <label>From</label>
                      <input
                        type="date"
                        value={customDraft.from}
                        onChange={e => setCustomDraft(d => ({ ...d, from: e.target.value }))}
                      />
                    </div>
                    <div className="cd-row">
                      <label>To</label>
                      <input
                        type="date"
                        value={customDraft.to}
                        onChange={e => setCustomDraft(d => ({ ...d, to: e.target.value }))}
                      />
                    </div>
                    <div className="cd-actions">
                      <button className="cd-cancel" onClick={() => setCustomOpen(false)}>Cancel</button>
                      <button
                        className="cd-apply"
                        disabled={!customDraft.from || !customDraft.to || customDraft.from > customDraft.to}
                        onClick={() => {
                          setCustomRange({ from: customDraft.from, to: customDraft.to });
                          setRange("custom");
                          setCustomOpen(false);
                        }}
                      >Apply</button>
                    </div>
                  </div>
                )}
              </div>
            </div>
          )}
          {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && (
            <>
              <div className="dfv2-toolbar-search">
                <svg className="dfv2-search-icon" viewBox="0 0 24 24" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                </svg>
                <input
                  ref={searchRef}
                  className="dfv2-search-input"
                  placeholder="Search all columns..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                />
                <span className="dfv2-search-kbd">⌘K</span>
              </div>

              <div className="dfv2-filter-wrap">
                <FilterTriggerButton
                  activeCount={activeFilterCount}
                  onClick={() => setFilterOpen((o) => !o)}
                  onClear={() => {
                    setAppliedFilters({});
                    setFilterMode("all");
                  }}
                />
                <DefectAdvancedFilterPopover
                  open={filterOpen}
                  defects={rangeDefects}
                  optionsDefects={visibleOptionsDefects}
                  teams={teams}
                  appliedFilters={appliedFilters}
                  filterMode={filterMode}
                  onApply={(draft, mode) => { setAppliedFilters(draft); setFilterMode(mode); }}
                  onApplyTemplate={(filters, mode) => { setAppliedFilters(filters); setFilterMode(mode); }}
                  onClose={() => setFilterOpen(false)}
                />
              </div>
            </>
          )}
          {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && (
            <div className="dfv2-colpick" ref={columnPickerRef}>
              <button
                className="dfv2-colpick-btn"
                title="Choose columns"
                aria-label="Choose columns"
                onClick={() => setColumnPickerOpen(o => !o)}
              >
                <svg viewBox="0 0 24 24">
                  <rect x="3" y="4" width="5" height="16" />
                  <rect x="10" y="4" width="5" height="16" />
                  <rect x="17" y="4" width="4" height="16" />
                </svg>
              </button>
              {columnPickerOpen && (
                <div className="dfv2-colpick-pop">
                  <div className="hd">
                    <span>Choose columns</span>
                    <span className="sub">ID, Title always visible</span>
                  </div>
                  <div className="opts">
                    {ISSUE_OPTIONAL_COLUMNS.map((col) => (
                      <label key={col.key} className="opt">
                        <input
                          type="checkbox"
                          checked={visibleColumns.includes(col.key)}
                          onChange={() => {
                            setVisibleColumns(prev =>
                              prev.includes(col.key)
                                ? prev.filter(k => k !== col.key)
                                : [...prev, col.key]
                            );
                          }}
                        />
                        <span>{col.label}</span>
                      </label>
                    ))}
                  </div>
                  <div className="act">
                    <button type="button" onClick={() => setVisibleColumns(allOptionalColumnKeys)}>Show all</button>
                    <button type="button" onClick={() => setColumnPickerOpen(false)}>Done</button>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Scrollable body — toolbar stays above this */}
      <div className="dfv2-scroll-body">

      {/* Lifecycle bar — now inside the scroll body so it scrolls away with
          the rest of the Dashboard tab instead of staying pinned at the top */}
      {viewTab === "dashboard" && !loading && (
        <LifecycleBar
          statusCounts={statusSummary.status_counts}
          onSelect={handleLcSelect}
          selected={lcSelected}
        />
      )}

      {loading && <div className="dfv2-loading">Loading…</div>}

      {/* Dashboard tab */}
      {viewTab === "dashboard" && !loading && (
        <>
          <div className="dfv2-dash" style={{ padding: "20px 28px 0" }}>
            <div className="dfv2-dash-head">
              <h2>Defects <em>— live dashboard</em></h2>
              <span className="eyebrow">trailing window · all projects</span>
              <span className="dash-rule" />
              <span className="range">Window <b>{rangeLabel}</b></span>
            </div>
            <KPIStrip stats={rangeStats} rangeLabel={rangeLabel} />
            <ComboChart data={comboData} />
            <AgingBar buckets={rangeStats.agingBuckets} openCount={rangeStats.openCount} />
            <div className="dfv2-dash-bottom">
              <StatusMixList statusCounts={statusSummary.status_counts} />
              <TopModulesList modules={rangeStats.topModules} />
            </div>
          </div>
          <BreakdownDonut sevData={rangeStats.sevData} totalCount={rangeStats.total} />
        </>
      )}

      {/* Executive Summary tab — Super Admin / Admin / AM (internal) only */}
      {viewTab === "executive-summary" && (
        <ExecutiveSummary data={execSummary} loading={execSummaryLoading} />
      )}

      {/* Issues / My view tab */}
      {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && issuesView === "table" && (
        <IssuesTable
          defects={paginatedDefects}
          user={user}
          loading={loading}
          visibleColumns={visibleColumns}
          onStatusChange={handleInlineStatusChange}
          onEditDefect={(rowDefect) => {
            setEditDrawerInitialTab("overview");
            setEditDrawerDefect(rowDefect);
          }}
        />
      )}

      {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && issuesView === "board" && !loading && (
        <DefectsBoard
          defects={tableDefects}
          onEditDefect={(rowDefect) => {
            setEditDrawerInitialTab("overview");
            setEditDrawerDefect(rowDefect);
          }}
          collapsedCols={collapsedCols}
          setCollapsedCols={setCollapsedCols}
          boardCols={boardColumns}
          draggingDefect={draggingDefect}
          dragOverStatus={dragOverStatus}
          onDragStartDefect={handleBoardDragStart}
          onDragOverStatus={handleBoardDragOver}
          onDropStatus={handleBoardDrop}
          onDragEndDefect={handleBoardDragEnd}
        />
      )}

      {(viewTab === "issues" || viewTab === "mine" || viewTab === "external") && issuesView === "table" && !loading && tableDefects.length > 0 && (
        <div className="dfv2-pagination">
          <div className="dfv2-pagination-left">
            <span className="dfv2-pagination-label">Rows per page:</span>
            <select
              className="dfv2-pagination-size"
              value={pageSize}
              onChange={(e) => {
                const v = e.target.value;
                setPageSize(v === "all" ? "all" : Number(v));
                setCurrentPage(1);
              }}
            >
              {[10, 25, 50, 100].map(n => <option key={n} value={n}>{n}</option>)}
              <option value="all">All</option>
            </select>
          </div>

          <div className="dfv2-pagination-right">
            {pageSize !== "all" ? (
              <>
                <span className="dfv2-pagination-info">
                  {`${pageStartIdx + 1}\u2013${Math.min(pageStartIdx + Number(pageSize), tableDefects.length)} of ${tableDefects.length}`}
                </span>
                <button
                  className="dfv2-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                  disabled={currentPage === 1}
                >
                  ‹
                </button>
                <span className="dfv2-pagination-page">{currentPage} / {totalPages}</span>
                <button
                  className="dfv2-pagination-btn"
                  onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                  disabled={currentPage >= totalPages}
                >
                  ›
                </button>
              </>
            ) : (
              <span className="dfv2-pagination-info">{tableDefects.length} rows</span>
            )}
          </div>
        </div>
      )}

      </div>{/* /dfv2-scroll-body */}

      {/* Sticky footer */}
      <FooterBar
        open={statusSummary.open}
        total={statusSummary.total}
        activeFilters={activeFilters}
        lastUpdated={lastUpdated}
      />

      {drawerOpen && (
        <DefectDrawer
          mode="create"
          user={user}
          showToast={showToast}
          onClose={() => setDrawerOpen(false)}
          onSaved={() => {
            setDrawerOpen(false);
            fetchDefectsForRange();
            refreshIssueCounts();
          }}
        />
      )}

      {editDrawerDefect && (
        <DefectDrawer
          mode="edit"
          defect={editDrawerDefect}
          initialTab={editDrawerInitialTab}
          user={user}
          showToast={showToast}
          onClose={() => setEditDrawerDefect(null)}
          onSaved={() => {
            setEditDrawerDefect(null);
            fetchDefectsForRange();
            refreshIssueCounts();
          }}
        />
      )}

      {pendingStatusChange && (
        <StatusTransitionReasonModal
          fromStatus={pendingStatusChange.defect.status}
          toStatus={pendingStatusChange.toStatus}
          onCancel={() => setPendingStatusChange(null)}
          onConfirm={(reason) => {
            commitDefectStatusChange(pendingStatusChange.defect, pendingStatusChange.toStatus, reason);
            setPendingStatusChange(null);
          }}
        />
      )}
    </div>
    </div>
  );
}
